import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Clock, CheckCircle, XCircle, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PasswordResetsPage = () => {
  const [resets, setResets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, pending: 0, used: 0, expired: 0 });

  useEffect(() => {
    fetchResets();
  }, []);

  const fetchResets = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/admin/password-resets?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResets(response.data.resets);
      
      // Calculate stats
      const now = new Date();
      const pending = response.data.resets.filter(r => !r.used && new Date(r.expires_at) > now).length;
      const used = response.data.resets.filter(r => r.used).length;
      const expired = response.data.resets.filter(r => !r.used && new Date(r.expires_at) <= now).length;
      
      setStats({
        total: response.data.resets.length,
        pending,
        used,
        expired
      });
    } catch (error) {
      toast.error('Failed to load password reset requests');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatus = (reset) => {
    const now = new Date();
    const expiresAt = new Date(reset.expires_at);
    
    if (reset.used) return { label: 'Used', color: 'bg-green-100 text-green-800', icon: CheckCircle };
    if (expiresAt < now) return { label: 'Expired', color: 'bg-red-100 text-red-800', icon: XCircle };
    return { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock };
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>
          Password Reset Requests
        </h1>
        <p className="text-gray-600 mt-2">Monitor and track password reset activity</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Total Requests</p>
              <p className="text-3xl font-bold" style={{ color: '#1B3240' }}>{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-3xl font-bold text-green-600">{stats.used}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Expired</p>
              <p className="text-3xl font-bold text-red-600">{stats.expired}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Resets Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Password Reset Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {resets.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No password reset requests yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">User</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Role</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Requested</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Expires</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">IP Address</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {resets.map((reset) => {
                    const status = getStatus(reset);
                    const StatusIcon = status.icon;
                    
                    return (
                      <tr key={reset.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium">{reset.user_email}</p>
                            {reset.used_at && (
                              <p className="text-xs text-gray-500">
                                Used: {formatDate(reset.used_at)}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">
                            {reset.user_role === 'resource_manager' ? 'RM' : 
                             reset.user_role === 'project_manager' ? 'PM' : 
                             reset.user_role}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {formatDate(reset.created_at)}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {formatDate(reset.expires_at)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center text-sm text-gray-600">
                            <MapPin className="w-3 h-3 mr-1" />
                            {reset.ip_address || 'N/A'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={status.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PasswordResetsPage;
